import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  HiOutlineArrowLeft,
  HiOutlineClock,
  HiOutlineCog6Tooth,
  HiOutlineIdentification,
  HiOutlineUser,
  HiOutlineCalendar,
  HiOutlineLink,
  HiOutlinePaperClip,
  HiOutlineTrash,
  HiOutlineSparkles,
  HiOutlineCheckCircle,
  HiOutlineArrowDown,
  HiOutlineClipboardDocumentList,
  HiOutlineCheck,
  HiOutlineXMark,
  HiOutlineArrowRight,
  HiOutlinePlus,
  HiOutlineArrowTopRightOnSquare,
  HiOutlinePrinter,
  HiOutlineTableCells,
} from "react-icons/hi2";
import * as XLSX from "xlsx";
import {
  authService,
  rcaService,
  plantService,
  masterDataService,
  aiService,
  machineService,
} from "../services";
import PageLoading from "../components/common/PageLoading";
import {
  showAppLoadingToast,
  dismissAppLoadingToast,
} from "../utils/appLoadingToast";
import "./FiveWhysAnalysis.css";

const FiveWhysAnalysis = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const rcaId = searchParams.get("rcaId");
  const currentUser = authService.getUser();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [generatingIndex, setGeneratingIndex] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(null);

  const [generatingQuestionIndex, setGeneratingQuestionIndex] = useState(null);
  const [questionSuggestions, setQuestionSuggestions] = useState([]);
  const [activeQuestionSuggestionIndex, setActiveQuestionSuggestionIndex] =
    useState(null);

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [nextAnalysisNo, setNextAnalysisNo] = useState("01");

  const [plants, setPlants] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const tenantId = authService.getTenantId();

  const [fiveWhys, setFiveWhys] = useState(["", "", "", "", ""]);
  const [whyQuestions, setWhyQuestions] = useState(["", "", "", "", ""]);
  const [questionEdited, setQuestionEdited] = useState([
    false,
    false,
    false,
    false,
    false,
  ]);

  const [formData, setFormData] = useState({
    problemStatement: "",
    plantId: "",
    machineId: "",
  });
  const [solution, setSolution] = useState("");
  const [generatingSolution, setGeneratingSolution] = useState(false);
  const [recordDetails, setRecordDetails] = useState({
    createdDate: new Date().toLocaleDateString(),
    createdBy: currentUser?.username || "Unknown",
    status: "New",
    assetName: "N/A",
    machineCode: "N/A",
    companyName: "",
    shift: "A", // Default shift
    referenceNo: "N/A",
  });

  const [extraData, setExtraData] = useState({
    lossName: "",
    immediateAction: "",
    actionConclusion: "",
    spareParts: "no", // 'replacement', 'no', or ''
  });

  const [rootCauseValidation, setRootCauseValidation] = useState({
    poorBasicCondition: {
      jh: false,
      pm: false,
      design: false,
      et: false,
      skill: false,
    },
    poorOperatingCondition: {
      jh: false,
      pm: false,
      design: false,
      et: false,
      skill: false,
    },
    deterioration: {
      jh: false,
      pm: false,
      design: false,
      et: false,
      skill: false,
    },
    weakDesign: {
      jh: false,
      pm: false,
      design: false,
      et: false,
      skill: false,
    },
    poorSkill: { jh: false, pm: false, design: false, et: false, skill: false },
  });

  const [actionItems, setActionItems] = useState({
    kaizen: false,
    opl: false,
    standards: false,
    pokaYoke: false,
    capa: false,
  });

  const [kaizenIdea, setKaizenIdea] = useState("");

  useEffect(() => {
    if (tenantId) {
      loadInitialData();
    }
    if (rcaId) {
      loadRcaData();
    } else {
      // Initialize with empty array of 5
      setFiveWhys(["", "", "", "", ""]);
    }
    if (location.state) {
      const {
        machineId,
        plantId,
        description,
        incidentRef,
        machineName,
        machineCode,
        shiftDate,
        shiftCode,
        lossName,
      } = location.state;
      setFormData((prev) => ({
        ...prev,
        machineId: machineId?.toString() || prev.machineId,
        plantId: plantId?.toString() || prev.plantId,
        problemStatement: description || prev.problemStatement,
      }));

      setRecordDetails((prev) => ({
        ...prev,
        assetName: machineName || prev.assetName,
        machineCode: machineCode || prev.machineCode,
        referenceNo: incidentRef || prev.referenceNo,
        createdDate: shiftDate
          ? new Date(shiftDate).toLocaleDateString()
          : prev.createdDate,
        shift: shiftCode || prev.shift,
      }));

      if (lossName) {
        setExtraData((prev) => ({ ...prev, lossName: lossName }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rcaId, tenantId, location.state]);

  // Auto-generate first question if problem statement is set via navigation
  useEffect(() => {
    if (location.state?.description && !rcaId) {
      // Short delay to ensure state is settled
      const timer = setTimeout(() => {
        // Pass the description directly to avoid closure staleness issues with formData
        handleSuggestQuestion(0, false, location.state.description);
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  // Handle auto-print from navigation
  useEffect(() => {
    if (!loading && location.state?.autoPrint) {
      // Small delay to ensure rendering is complete
      const timer = setTimeout(() => {
        handlePrint();
        // clear state to prevent loop if desired, but handlePrint is just window.print()
        // We can optionally clear state: navigate(".", { replace: true, state: {} });
      }, 1000);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, location.state]);

  const loadInitialData = async () => {
    try {
      const [plantsData, statusesData] = await Promise.all([
        plantService.getAllPlants(),
        masterDataService.getStatuses?.(tenantId) || Promise.resolve([]),
      ]);
      setPlants(plantsData || []);
      const rcaStatuses = statusesData.filter(
        (s) => !s.statusType || s.statusType === "RCA",
      );
      setStatuses(rcaStatuses);

      // Auto-select plant if only 1 exists (for new entries)
      if (!rcaId && !location.state?.plantId && plantsData?.length === 1) {
        setFormData(prev => ({ ...prev, plantId: plantsData[0].plantId.toString() }));
      }

      // Fetch Company Name from Tenant
      const tenantStr = localStorage.getItem("tenant");
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        setRecordDetails((prev) => ({
          ...prev,
          companyName: tenant.tenantName || "Company Name",
        }));
      }

      // Calculate Shift based on current time if new record
      if (!rcaId && !location.state?.shiftCode) {
        const hour = new Date().getHours();
        let shift = "A";
        if (hour >= 6 && hour < 14) shift = "A";
        else if (hour >= 14 && hour < 22) shift = "B";
        else shift = "C";
        setRecordDetails((prev) => ({ ...prev, shift }));
      }

      // Calculate Next Analysis No
      if (!rcaId) {
        try {
          const allRecords = await rcaService.getRcaRecords();
          const records = Array.isArray(allRecords)
            ? allRecords
            : allRecords.items || [];
          const maxId = records.reduce(
            (max, r) => (r.rcaId > max ? r.rcaId : max),
            0,
          );
          setNextAnalysisNo(String(maxId + 1).padStart(2, "0"));
        } catch (e) {
          console.error("Error fetching next analysis no", e);
        }
      }
    } catch (err) {
      console.error("Error loading initial data:", err);
      setError("Failed to load data");
    }
  };

  const loadRcaData = async () => {
    try {
      setLoading(true);
      const record = await rcaService.getRcaRecordById(rcaId);

      setFormData({
        problemStatement: record.problemDesc || "",
        plantId: record.plantId?.toString() || "",
        machineId: record.machineId?.toString() || "",
      });

      let assetName = "N/A";
      let machineCode = "N/A";
      if (record.machineId) {
        try {
          const machine = await machineService.getMachineById(record.machineId);
          assetName =
            machine.machineName || machine.name || `Asset-${record.machineId}`;
          machineCode =
            machine.machineCode ||
            machine.code ||
            machine.machineNumber ||
            "N/A";
        } catch (mErr) {
          console.error("Error loading machine details:", mErr);
          assetName = `Asset-${record.machineId}`; // Fallback
        }
      }

      // Parse fields from rootCause
      let lossName = "";
      let immediateAction = "";
      let actionConclusion = "";
      let spareParts = "no";
      let shift = "A";
      let referenceNo = "N/A";

      if (record.rootCause) {
        // Parse metadata
        const lossMatch = record.rootCause.match(/Loss Name:\s*(.*?)(?:\n|$)/);
        if (lossMatch) lossName = lossMatch[1].trim();

        const actionMatch = record.rootCause.match(
          /Immediate Action:\s*(.*?)(?:\n|$)/,
        );
        if (actionMatch) immediateAction = actionMatch[1].trim();

        const conclusionMatch = record.rootCause.match(
          /Action Conclusion:\s*(.*?)(?:\n|$)/,
        );
        if (conclusionMatch) actionConclusion = conclusionMatch[1].trim();

        const spareMatch = record.rootCause.match(
          /Spare Part Replacement:\s*(.*?)(?:\n|$)/,
        );
        if (spareMatch)
          spareParts =
            spareMatch[1].trim().toLowerCase() === "yes" ? "replacement" : "no";

        const shiftMatch = record.rootCause.match(/Shift:\s*(.*?)(?:\n|$)/);
        if (shiftMatch) shift = shiftMatch[1].trim();

        const refMatch = record.rootCause.match(
          /Reference No:\s*(.*?)(?:\n|$)/,
        );
        if (refMatch) referenceNo = refMatch[1].trim();

        const validationMatch = record.rootCause.match(
          /Root Cause Validation: (.*?)(?:\n|$)/,
        );
        if (validationMatch) {
          try {
            setRootCauseValidation(JSON.parse(validationMatch[1]));
          } catch (e) {
            console.error("Error parsing validation", e);
          }
        }

        const actionsMatch = record.rootCause.match(
          /Action Items: (.*?)(?:\n|$)/,
        );
        if (actionsMatch) {
          try {
            setActionItems(JSON.parse(actionsMatch[1]));
          } catch (e) {
            console.error("Error parsing actions", e);
          }
        }

        const kaizenMatch = record.rootCause.match(
          /Kaizen Idea: (.*?)(?:\n|$)/,
        );
        if (kaizenMatch) {
          setKaizenIdea(kaizenMatch[1]);
        }
      }

      const tenantStr = localStorage.getItem("tenant");
      const savedTenantName = tenantStr
        ? JSON.parse(tenantStr).tenantName
        : "Company Name";

      setRecordDetails({
        createdDate: record.createdDate
          ? new Date(record.createdDate).toLocaleDateString()
          : new Date().toLocaleDateString(),
        createdBy: record.createdByName || currentUser?.username || "Unknown",
        status: record.statusName || "New",
        assetName: assetName,
        machineCode: machineCode,
        companyName: savedTenantName,
        shift: shift,
        referenceNo: referenceNo,
      });

      setExtraData({
        lossName,
        immediateAction,
        actionConclusion,
        spareParts,
      });

      // Parse 5 Whys data from rootCause if it exists
      if (record.rootCause) {
        const whysMatch = record.rootCause.match(
          /5 Whys Analysis:\s*([\s\S]*?)(?:\n\nRecommended Solution:|$)/,
        );
        if (whysMatch) {
          const whysText = whysMatch[1];
          const whys = [];
          for (let i = 1; i <= 5; i++) {
            const regex = new RegExp(`Why ${i}:\\s*([^\\n]+)`, "i");
            const match = whysText.match(regex);
            whys.push(match ? match[1].trim() : "");
          }
          // Ensure we have at least what was parsed, pad to 5 if needed
          while (whys.length < 5) whys.push("");
          setFiveWhys(whys);

          setWhyQuestions(
            new Array(whys.length).fill("Why did the previous cause happen?"),
          );
          setQuestionEdited(new Array(whys.length).fill(false));
        }

        // Parse Solution from rootCause if it exists
        const solutionMatch = record.rootCause.match(
          /Recommended Solution:\s*([\s\S]*?)(?:\n\n|$)/,
        );
        if (solutionMatch) {
          setSolution(solutionMatch[1].trim());
        }
      }
    } catch (err) {
      console.error("Error loading RCA data:", err);
      setError("Failed to load RCA data");
    } finally {
      setLoading(false);
    }
  };

  const handleFiveWhyChange = (index, value) => {
    const updated = [...fiveWhys];
    updated[index] = value;
    setFiveWhys(updated);
  };

  const addWhy = () => {
    setFiveWhys([...fiveWhys, ""]);
    setWhyQuestions([...whyQuestions, ""]);
    setQuestionEdited([...questionEdited, false]);
  };

  const removeWhy = (index) => {
    if (fiveWhys.length > 1) {
      const updated = fiveWhys.filter((_, i) => i !== index);
      setFiveWhys(updated);
      setWhyQuestions(whyQuestions.filter((_, i) => i !== index));
      setQuestionEdited(questionEdited.filter((_, i) => i !== index));
    }
  };

  const handleQuestionChange = (index, value) => {
    const updated = [...whyQuestions];
    updated[index] = value;
    setWhyQuestions(updated);

    const updatedEdited = [...questionEdited];
    updatedEdited[index] = true;
    setQuestionEdited(updatedEdited);
  };

  const handleFormDataChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSuggestWhy = async (index, append = false) => {
    if (!formData.problemStatement || formData.problemStatement.trim() === "") {
      setError("Please enter a problem statement first.");
      return;
    }

    try {
      setGeneratingIndex(index);
      setError("");
      showAppLoadingToast("Getting AI suggestions…");

      const previousWhys = fiveWhys.slice(0, index);
      const response = await aiService.generateNextWhy(
        formData.problemStatement,
        previousWhys,
      );

      let newSuggestions = [];
      if (Array.isArray(response)) {
        newSuggestions = response;
      } else if (typeof response === "object" && response.suggestions) {
        newSuggestions = response.suggestions;
      } else if (typeof response === "string") {
        newSuggestions = [response];
      } else if (response.answer) {
        newSuggestions = [response.answer];
      }

      if (append) {
        // Filter out duplicates
        const uniqueNew = newSuggestions.filter(
          (ns) => !suggestions.includes(ns),
        );
        if (uniqueNew.length === 0) {
          setError("No new suggestions found.");
          // Clear error after 3s
          setTimeout(() => setError(""), 3000);
        }
        setSuggestions((prev) => [...prev, ...uniqueNew]);
      } else {
        setSuggestions(newSuggestions);
        setActiveSuggestionIndex(index);
      }
    } catch (err) {
      console.error("AI Suggestion Error:", err);
      // Mock for dev
      const mockOptions = [
        "The hydraulic fluid was contaminated.",
        "The pump filter was clogged.",
        "The intake valve failed to open.",
      ];
      setSuggestions(mockOptions);
      setActiveSuggestionIndex(index);
    } finally {
      dismissAppLoadingToast();
      setGeneratingIndex(null);
    }
  };

  const handleSelectSuggestion = (index, value) => {
    handleFiveWhyChange(index, value);
    setActiveSuggestionIndex(null);
    setSuggestions([]);
  };

  const closeSuggestions = () => {
    setActiveSuggestionIndex(null);
    setSuggestions([]);
  };

  const handleSuggestQuestion = async (
    index,
    append = false,
    overrideContext = null,
  ) => {
    const context =
      overrideContext ||
      (index === 0 ? formData.problemStatement : fiveWhys[index - 1]);
    if (!context || context.trim() === "") {
      setError(
        index === 0
          ? "Please enter a problem statement."
          : "Please complete the previous answer.",
      );
      return;
    }

    try {
      setGeneratingQuestionIndex(index);
      setError("");
      showAppLoadingToast("Generating question…");

      const response = await aiService.generateWhyQuestion(context);

      let newSuggestions = [];
      if (Array.isArray(response)) {
        newSuggestions = response;
      } else if (typeof response === "string") {
        newSuggestions = [response];
      } else if (response.suggestions) {
        newSuggestions = response.suggestions;
      }

      if (append) {
        // Filter out duplicates
        const uniqueNew = newSuggestions.filter(
          (ns) => !questionSuggestions.includes(ns),
        );
        if (uniqueNew.length === 0) {
          setError("No new suggestions found.");
          setTimeout(() => setError(""), 3000);
        }
        setQuestionSuggestions((prev) => [...prev, ...uniqueNew]);
      } else {
        setQuestionSuggestions(newSuggestions);
        setActiveQuestionSuggestionIndex(index);
      }
    } catch (err) {
      console.error("AI Question Error:", err);
      const mockQuestions = [
        `Why did ${context.substring(0, 20)}... happen?`,
        `What caused ${context.substring(0, 20)}...?`,
        `What led to ${context.substring(0, 20)}...?`,
      ];
      setQuestionSuggestions(mockQuestions);
      setActiveQuestionSuggestionIndex(index);
    } finally {
      dismissAppLoadingToast();
      setGeneratingQuestionIndex(null);
    }
  };

  const handleSelectQuestionSuggestion = (index, value) => {
    handleQuestionChange(index, value);
    setActiveQuestionSuggestionIndex(null);
    setQuestionSuggestions([]);
  };

  const closeQuestionSuggestions = () => {
    setActiveQuestionSuggestionIndex(null);
    setQuestionSuggestions([]);
  };

  const handleNavigateToKaizen = () => {
    const lastWhy = fiveWhys.filter((w) => w && w.trim()).pop() || "";
    navigate("/home/kaizen-entry", {
      state: {
        title: formData.problemStatement
          ? formData.problemStatement.substring(0, 100)
          : "Kaizen from RCA",
        problemStatement: formData.problemStatement,
        currentState: lastWhy,
        proposedIdea: kaizenIdea,
        machineId: formData.machineId,
        referenceNumber: rcaId || nextAnalysisNo,
        moduleName: "FiveWhys",
      },
    });
  };

  const handleNavigateToOPL = () => {
    const analysisId = rcaId || nextAnalysisNo;
    const problem = formData.problemStatement || "";
    navigate("/home/opl", {
      state: {
        create: true,
        title: `OPL-${analysisId}: ${problem.substring(0, 50)}`,
        objective: problem,
        machineId: formData.machineId,
        referenceNumber: analysisId,
        moduleName: "FiveWhys",
      },
    });
  };

  const handleNavigateToCAPA = () => {
    const rootCause = fiveWhys.filter((w) => w && w.trim()).pop() || "";
    navigate("/home/capa-entry", {
      state: {
        rcaId: rcaId,
        problemDescription: formData.problemStatement,
        rootCause: rootCause,
        machineId: formData.machineId,
        plantId: formData.plantId,
      },
    });
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportExcel = () => {
    // 1. Prepare Data
    const data = [
      // Header Info
      [
        recordDetails.companyName,
        null,
        `Equipment Name : ${recordDetails.assetName}`,
        null,
        `Equipment No: ${recordDetails.machineCode}`,
      ],
      [
        "WHY - WHY ANALYSIS SHEET",
        null,
        `Analysis no    ${rcaId ? String(rcaId).padStart(2, "0") : nextAnalysisNo}`,
        null,
        `Date & Shift\n${recordDetails.createdDate} & ${recordDetails.shift}`,
        `Action done by :\n${recordDetails.createdBy || "TPP PERFUMES"}`,
      ],
      // Loss Name & Spare Parts Checkbox (Top)
      [
        `Loss Name : ${extraData.lossName || ""}`,
        null,
        null,
        null,
        null,
        `In case of spare part replacement  [ ${extraData.spareParts === "replacement" ? "✓" : " "
        } ]`,
      ],
      // Problem Statement & Spare Parts Checkbox (Bottom)
      [
        "1. Breakdown, Physical phenomenon:",
        null,
        null,
        null,
        null,
        `In case of spare part No replacement  [ ${extraData.spareParts === "no" ? "✓" : " "
        } ]`,
      ],
      [formData.problemStatement || "", null, null, null, null, null],
      // Immediate Action Header
      [
        "2. What is your immediate action? (For correcting the problem)",
        null,
        null, // Merged
        "2.1 How immediate action concluded : (for arriving at the problem)",
      ],
      // Immediate Action Content
      [
        extraData.immediateAction || "",
        null,
        null,
        extraData.actionConclusion || "",
      ],
      // 5 Whys Table Header
      ["4.\nWHY", "Why did you take above action", "Due to", "Action"],
    ];

    // 5 Whys Rows
    const maxRows = 5;
    for (let i = 0; i < maxRows; i++) {
      const rowNum = i + 1;
      const whyAnswer = fiveWhys[i] || "";
      const whyQuestion =
        whyQuestions[i] || "Why did the previous cause happen?";

      // Image 2 format: Col 1: Number, Col 2: Question (Why did prev cause happen?), Col 3: Answer (Due to...)
      // Note: The image shows the *question* in column 2 and the *answer* in column 3.
      // However, in our app, `fiveWhys` array holds the answers. `whyQuestions` holds the questions.
      // If it's the first row, question is usually "Why did [Problem] happen?" or generic.

      // Logic to match image:
      // 1 | Why did the previous cause happen? | Raw material quantities were incorrect... | [Empty]

      data.push([
        rowNum,
        whyQuestion,
        whyAnswer,
        "", // Action column is empty in image
      ]);
    }

    // Root Cause
    const rootCause = fiveWhys.filter((w) => w && w.trim()).pop() || "";
    data.push([`ROOT CAUSE : ${rootCause}`, null, null, null]);

    // Validation Matrix Header
    const matrixHeader = [
      "5. Root cause is one of the following 5 items.",
      "JH",
      "PM",
      "Design",
      "E&T",
      "Skill",
    ];
    data.push(matrixHeader);

    // Validation Matrix Rows
    const categories = [
      { key: "poorBasicCondition", label: "Poor Basic Condition" },
      { key: "poorOperatingCondition", label: "Poor operating Condition" },
      { key: "deterioration", label: "Deterioration" },
      { key: "weakDesign", label: "Weak design" },
      { key: "poorSkill", label: "Poor Skill" },
    ];

    categories.forEach((cat) => {
      const row = [cat.label];
      ["jh", "pm", "design", "et", "skill"].forEach((sub) => {
        row.push(rootCauseValidation[cat.key]?.[sub] ? "✓" : "");
      });
      data.push(row);
    });

    // Action Items (Kaizen / OPL etc)
    const kaizenCheck = actionItems.kaizen ? "✓" : " ";
    const oplCheck = actionItems.opl ? "✓" : " ";
    const stdCheck = actionItems.standards ? "✓" : " "; // Included in ...
    const pyCheck = actionItems.pokaYoke ? "✓" : " ";
    const capaCheck = actionItems.capa ? "✓" : " ";

    // Row 6 in image (approx)
    // 6 Kaizen [ ]  One Point Lesson [ ]  Included in (...) [ ]  Poka Yoke [ ]  CAPA [ ]
    data.push([
      `6 Kaizen [ ${kaizenCheck} ]    One Point Lesson [ ${oplCheck} ]    Included in (TBM / TBI / CBM / PM stds.) [ ${stdCheck} ]    Poka Yoke [ ${pyCheck} ]    CAPA [ ${capaCheck} ]`,
    ]);

    // Kaizen Idea
    data.push([`7. Kaizen Idea : ${kaizenIdea || ""}`]);

    // Create Sheet
    const ws = XLSX.utils.aoa_to_sheet(data);

    // Define Merges
    // s: start (c: col, r: row), e: end
    // NOTE: Row indices are 0-based

    const merges = [
      // Row 0: Company Name (A1:B1) - Example merge
      { s: { r: 0, c: 0 }, e: { r: 0, c: 1 } }, // Company Name
      { s: { r: 0, c: 2 }, e: { r: 0, c: 3 } }, // Equipment Name

      // Row 1: Title (A2:B2), Analysis No (C2), Date/Shift (D2:E2), Action Done By (F2) (Col indices might need adj)
      // Let's check columns used. We pushed 6 items in Row 2 approx?
      // Row 2 data: ["WHY ANALYSIS", null, "Analysis no", null, "Date", "Action by"]
      // Let's assume a 6-column grid for safety (A-F) or 4 main cols merged.

      // Adjusting layout slightly for better fit in standard grid
      // Columns: A, B, C, D, E, F

      // Row 1
      { s: { r: 1, c: 0 }, e: { r: 1, c: 1 } }, // WHY - WHY ...
      { s: { r: 1, c: 2 }, e: { r: 1, c: 3 } }, // Analysis No

      // Row 2 (Loss Name)
      { s: { r: 2, c: 0 }, e: { r: 2, c: 4 } }, // Loss Name spans wide

      // Row 3 (Breakdown Header)
      { s: { r: 3, c: 0 }, e: { r: 3, c: 4 } }, // 1. Breakdown...

      // Row 4 (Problem Statement) -> This is data row index 4
      { s: { r: 4, c: 0 }, e: { r: 4, c: 5 } }, // Full width problem desc

      // Row 5 (Immediate Action Header)
      // Split: Col 0-2 (Left), Col 3-5 (Right)
      { s: { r: 5, c: 0 }, e: { r: 5, c: 2 } },
      { s: { r: 5, c: 3 }, e: { r: 5, c: 5 } },

      // Row 6 (Immediate Action Data)
      { s: { r: 6, c: 0 }, e: { r: 6, c: 2 } },
      { s: { r: 6, c: 3 }, e: { r: 6, c: 5 } },

      // Root Cause Row (After 5 whys) -> Index = 7 (header) + 5 (whys) = 12
      { s: { r: 12, c: 0 }, e: { r: 12, c: 5 } },

      // Matrix Header -> Index 13
      { s: { r: 13, c: 0 }, e: { r: 13, c: 0 } }, // "5. Root cause..." (Keep in A)

      // Kaizen Checkboxes Row -> Index 13 + 1 (header) + 5 (categories) = 19
      { s: { r: 19, c: 0 }, e: { r: 19, c: 5 } },

      // Kaizen Idea Row -> Index 20
      { s: { r: 20, c: 0 }, e: { r: 20, c: 5 } },
    ];

    ws["!merges"] = merges;

    // Set Column Widths
    ws["!cols"] = [
      { wch: 5 }, // A: No / Label
      { wch: 40 }, // B: Question / Category
      { wch: 40 }, // C: Answer / JH
      { wch: 20 }, // D: Action / PM
      { wch: 20 }, // E: Design
      { wch: 30 }, // F: Extra
    ];

    // Create Workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Analysis Sheet");

    // Save
    const filename = `RCA_${rcaId || "New"}_Sheet.xlsx`;
    XLSX.writeFile(wb, filename);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      setSuccess("");
      setFormErrors({});

      // Validation logic same as before...
      if (!rcaId) {
        // Ideally validation here... logic preserved from original file
      }

      const filledWhys = fiveWhys.filter((w) => w && w.trim().length > 0);
      if (filledWhys.length === 0) {
        setError('Please fill at least one "Why" question');
        setSaving(false);
        return;
      }

      // Validation for Plant
      const finalPlantId =
        parseInt(formData.plantId) || plants[0]?.plantId || 0;
      if (finalPlantId === 0) {
        setError(
          "No Plant selected or available (Master Data missing). Cannot save.",
        );
        setSaving(false);
        return;
      }

      const whyLines = filledWhys
        .map((w, idx) => `Why ${idx + 1}: ${w.trim()}`)
        .join("\n");

      let payload = {};
      const defaultStatus = statuses.length > 0 ? statuses[0].statusId : null;

      const problemDesc = formData.problemStatement?.trim() || "Problem Report";

      // Construct rich rootCause
      const metaData = [
        `Loss Name: ${extraData.lossName}`,
        `Immediate Action: ${extraData.immediateAction}`,
        `Action Conclusion: ${extraData.actionConclusion}`,
        `Spare Part Replacement: ${extraData.spareParts === "replacement" ? "Yes" : "No"}`,
        `Shift: ${recordDetails.shift}`,
        `Reference No: ${recordDetails.referenceNo}`,
        `Root Cause Validation: ${JSON.stringify(rootCauseValidation)}`,
        `Action Items: ${JSON.stringify(actionItems)}`,
        `Kaizen Idea: ${kaizenIdea}`,
      ].join("\n");

      const rootCauseText = `${metaData}

5 Whys Analysis:
${whyLines}

Recommended Solution:
${solution.trim() || "N/A"}`;

      if (rcaId) {
        // Update
        const record = await rcaService.getRcaRecordById(rcaId);
        payload = {
          rcaId: parseInt(rcaId, 10),
          plantId: record.plantId,
          machineId: record.machineId,
          triggerReasonId: record.triggerReasonId || null,
          triggerReason: record.triggerReason || "",
          problemDesc: problemDesc,
          rootCause: rootCauseText,
          method: "5 Whys",
          statusId:
            record.statusId ||
            (statuses.length > 0 ? statuses[0].statusId : null),
          createdBy: record.createdBy,
        };
        await rcaService.updateRcaRecord(rcaId, payload);
      } else {
        // Create
        // Check if no statuses, warn but allow proceed (backend must handle null)
        if (!defaultStatus) {
          console.warn(
            "No Statuses defined in Master Data. Proceeding with null StatusId.",
          );
        }

        const userId = currentUser?.userId || 0;
        if (userId === 0) {
          console.warn(
            "User ID missing, defaulting to 0. This may fail if User ID 0 does not exist.",
          );
        }

        // Ensure statusId is a valid integer
        const finalStatusId = defaultStatus
          ? parseInt(defaultStatus, 10)
          : null;

        payload = {
          plantId: finalPlantId,
          machineId: formData.machineId ? parseInt(formData.machineId) : null,
          triggerReason: "",
          triggerReasonId: null,
          problemDesc: problemDesc,
          rootCause: rootCauseText,
          method: "5 Whys",
          statusId: finalStatusId,
          createdBy: userId,
        };
        await rcaService.createRcaRecord(payload);
      }

      setSuccess("5 Whys analysis saved successfully!");
      setTimeout(() => navigate("/home/rca"), 1500);
    } catch (err) {
      console.error("Error saving:", err);
      if (err.response && err.response.data && err.response.data.message) {
        setError(`Failed to save: ${err.response.data.message}`);
      } else {
        setError("Failed to save analysis.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="pagecontainer five-whys-analysis-page">
        <PageLoading message="Loading analysis…" />
      </div>
    );
  }

  return (
    <div className="pagecontainer five-whys-analysis-page">
      <div className="five-whys-main-content">
        {/* Header */}
        <header className="page-header">
          <div className="header-content">

            <h1>Root Cause Analysis</h1>
            <p>Investigating {formData.problemStatement || "New Issue"}</p>
          </div>
          <div className="header-actions">
            <button className="btn-fw-secondary" onClick={handlePrint}>
              <HiOutlinePrinter className="icon" /> Print
            </button>
            <button className="btn-fw-secondary" onClick={handleExportExcel}>
              <HiOutlineTableCells className="icon" /> Export Excel
            </button>
            <button
              className="btn-fw-secondary"
              onClick={() => navigate("/home/rca")}
            >
              <HiOutlineClock className="icon" /> View History
            </button>
            <button
              className="btn-fw-primary"
              onClick={handleSave}
              disabled={saving}
            >
              <HiOutlineCheckCircle className="icon" />{" "}
              {saving ? "Saving..." : "Save Analysis"}
            </button>
          </div>
        </header>

        {error && (
          <div
            className="error-message"
            style={{ marginBottom: "1rem", color: "red" }}
          >
            {error}
          </div>
        )}
        {success && (
          <div
            className="success-message"
            style={{ marginBottom: "1rem", color: "green" }}
          >
            {success}
          </div>
        )}

        {/* Incident Context Card */}
        <div className="fw-card">
          <div className="fw-card-header">
            <h3 className="fw-card-title">Incident Context</h3>
            <span className="fw-status-badge">{recordDetails.status}</span>
          </div>
          <div className="fw-context-grid">
            <div className="fw-context-item">
              <p className="label">Company Name</p>
              <div className="fw-context-value">
                <span className="value">
                  {recordDetails.companyName || "N/A"}
                </span>
              </div>
            </div>
            <div className="fw-context-item">
              <p className="label">Equipment Name</p>
              <div className="fw-context-value">
                <HiOutlineCog6Tooth className="icon" />
                <span className="value">{recordDetails.assetName}</span>
              </div>
            </div>
            <div className="fw-context-item">
              <p className="label">Equipment No</p>
              <div className="fw-context-value">
                <span className="value">{recordDetails.machineCode}</span>
              </div>
            </div>
            <div className="fw-context-item">
              <p className="label">Reference No</p>
              <div className="fw-context-value">
                <HiOutlineCog6Tooth className="icon" />
                <span className="value">{recordDetails.referenceNo}</span>
              </div>
            </div>
            <div className="fw-context-item">
              <p className="label">Analysis No</p>
              <div className="fw-context-value">
                <span className="value text-primary">
                  {rcaId ? String(rcaId).padStart(2, "0") : nextAnalysisNo}
                </span>
              </div>
            </div>
            <div className="fw-context-item">
              <p className="label">Date</p>
              <div className="fw-context-value">
                <HiOutlineCalendar className="icon" />
                <span className="value">{recordDetails.createdDate}</span>
              </div>
            </div>
            <div className="fw-context-item">
              <p className="label">Shift</p>
              <div className="fw-context-value">
                <select
                  className="fw-input"
                  style={{
                    padding: "0.25rem 0.5rem",
                    width: "auto",
                    height: "auto",
                    minWidth: "80px",
                  }}
                  value={recordDetails.shift}
                  onChange={(e) =>
                    setRecordDetails((p) => ({ ...p, shift: e.target.value }))
                  }
                >
                  <option value="A">A</option>
                  <option value="B">B</option>
                  <option value="C">C</option>
                </select>
              </div>
            </div>
            <div className="fw-context-item">
              <p className="label">Action Done By</p>
              <div className="fw-context-value">
                <HiOutlineUser className="icon" />
                <span className="value">{recordDetails.createdBy}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Problem Definition Card */}
        <div className="fw-card" style={{ marginTop: "1.0rem" }}>
          <div className="fw-card-header">
            <h3 className="fw-card-title">Problem Definition</h3>
          </div>
          <div
            style={{
              padding: "1.5rem",
              display: "flex",
              flexDirection: "column",
              gap: "1.5rem",
            }}
          >
            {/* Loss Name */}
            <div className="fw-input-group">
              <label className="fw-input-label">Loss Name</label>
              <input
                type="text"
                className="fw-input"
                value={extraData.lossName}
                onChange={(e) =>
                  setExtraData((prev) => ({
                    ...prev,
                    lossName: e.target.value,
                  }))
                }
                placeholder="e.g. Rework Loss"
                style={{ maxWidth: "100%" }}
              />
            </div>

            {/* Problem Statement & Spare Parts Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                gap: "1.5rem",
              }}
            >
              <div className="fw-input-group">
                <label className="fw-input-label">
                  1. Breakdown, Physical Phenomenon
                </label>
                <div style={{ position: "relative" }}>
                  <textarea
                    className="fw-problem-textarea"
                    placeholder="Describe the specific failure observed..."
                    value={formData.problemStatement}
                    onChange={(e) =>
                      handleFormDataChange("problemStatement", e.target.value)
                    }
                    style={{ minHeight: "100px" }}
                  />
                  {/* Spare Parts Overlay/Section */}
                  <div
                    style={{
                      marginTop: "1rem",
                      padding: "1rem",
                      backgroundColor: "var(--slate-50)",
                      borderRadius: "var(--border-radius-lg)",
                      border: "1px solid var(--slate-200)",
                      display: "flex",
                      gap: "2rem",
                      flexWrap: "wrap",
                      alignItems: "center",
                    }}
                  >
                    <span
                      className="fw-input-label"
                      style={{ marginBottom: 0 }}
                    >
                      Spare Part Strategy:
                    </span>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="spareParts"
                        checked={extraData.spareParts === "replacement"}
                        onChange={() =>
                          setExtraData((p) => ({
                            ...p,
                            spareParts: "replacement",
                          }))
                        }
                        className="accent-primary"
                      />
                      <span
                        style={{
                          fontSize: "0.875rem",
                          color: "var(--slate-700)",
                        }}
                      >
                        Replacement Needed
                      </span>
                    </label>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="radio"
                        name="spareParts"
                        checked={extraData.spareParts === "no"}
                        onChange={() =>
                          setExtraData((p) => ({ ...p, spareParts: "no" }))
                        }
                        className="accent-primary"
                      />
                      <span
                        style={{
                          fontSize: "0.875rem",
                          color: "var(--slate-700)",
                        }}
                      >
                        No Replacement
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Immediate Actions Card */}
        <div className="fw-card" style={{ marginTop: "1.0rem" }}>
          <div className="fw-card-header">
            <h3 className="fw-card-title">Immediate Actions & Conclusion</h3>
          </div>
          <div style={{ padding: "1.5rem" }}>
            <div className="fw-step-grid">
              {/* Immediate Action */}
              <div className="fw-input-group">
                <label className="fw-input-label">
                  2. Immediate Action (Correction)
                </label>
                <textarea
                  className="fw-input"
                  rows={3}
                  placeholder="What was done immediately to fix the problem?"
                  value={extraData.immediateAction}
                  onChange={(e) =>
                    setExtraData((prev) => ({
                      ...prev,
                      immediateAction: e.target.value,
                    }))
                  }
                />
              </div>
              {/* Conclusion */}
              <div className="fw-input-group">
                <label className="fw-input-label">2.1 Action Conclusion</label>
                <textarea
                  className="fw-input"
                  rows={3}
                  placeholder="How was the action concluded?"
                  value={extraData.actionConclusion}
                  onChange={(e) =>
                    setExtraData((prev) => ({
                      ...prev,
                      actionConclusion: e.target.value,
                    }))
                  }
                />
              </div>
            </div>
          </div>
        </div>

        {/* 5 Whys Timeline */}
        <div className="fw-timeline-container" style={{ marginTop: "2rem" }}>
          <div className="fw-timeline-line"></div>

          <div className="fw-step-grid-container">
            {fiveWhys.map((why, index) => (
              <div
                key={index}
                className="fw-step-wrapper"
                style={{
                  zIndex:
                    activeSuggestionIndex === index ||
                      activeQuestionSuggestionIndex === index
                      ? 10
                      : 1,
                }}
              >
                <div className="fw-step-card">
                  <div className="fw-step-header">
                    <span className="fw-step-number">{index + 1}</span>
                    <div className="fw-step-actions">
                      <button
                        className="btn-icon"
                        title="Attach Evidence"
                        onClick={() =>
                          document
                            .getElementById(`file-upload-${index}`)
                            .click()
                        }
                      >
                        <HiOutlinePaperClip />
                      </button>
                      <input
                        type="file"
                        id={`file-upload-${index}`}
                        style={{ display: "none" }}
                        onChange={(e) => {
                          if (e.target.files?.[0]) {
                            // Placeholder for attachment logic
                            console.log(
                              `File attached to step ${index + 1}:`,
                              e.target.files[0].name,
                            );
                            // In a real app, we'd save this to state
                          }
                        }}
                      />
                      {fiveWhys.length > 1 && (
                        <button
                          className="btn-icon delete"
                          onClick={() => removeWhy(index)}
                          title="Delete Step"
                        >
                          <HiOutlineTrash />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="fw-step-grid">
                    <div className="fw-input-group">
                      <label className="fw-input-label">The Why Question</label>
                      <div className="fw-input-wrapper">
                        <input
                          type="text"
                          className="fw-input"
                          value={whyQuestions[index] || ""}
                          onChange={(e) =>
                            handleQuestionChange(index, e.target.value)
                          }
                          placeholder="Type question here..."
                        />
                        <button
                          className="btn-generate-answer"
                          onClick={() => handleSuggestQuestion(index)}
                          title="Generate Question with AI"
                        >
                          <span style={{ marginRight: "4px" }}>Generate</span>
                          <HiOutlineSparkles className="icon" />
                        </button>

                        {/* AI Question Suggestions Dropdown */}
                        {activeQuestionSuggestionIndex === index &&
                          questionSuggestions.length > 0 && (
                            <div className="ai-suggestions-dropdown">
                              <div className="ai-suggestions-header">
                                <HiOutlineSparkles
                                  style={{ color: "var(--primary)" }}
                                />
                                <span className="title">AI Suggestions</span>
                                <button
                                  style={{
                                    marginLeft: "auto",
                                    border: "none",
                                    background: "none",
                                    cursor: "pointer",
                                  }}
                                  onClick={closeQuestionSuggestions}
                                >
                                  <HiOutlineXMark />
                                </button>
                              </div>
                              <ul className="ai-suggestion-list">
                                {questionSuggestions.map((s, i) => (
                                  <li key={i}>
                                    <button
                                      className="ai-suggestion-item"
                                      onClick={() =>
                                        handleSelectQuestionSuggestion(index, s)
                                      }
                                    >
                                      <HiOutlineArrowRight className="icon" />
                                      {s}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                              <button
                                className="ai-show-more-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSuggestQuestion(index, true);
                                }}
                                disabled={generatingQuestionIndex === index}
                              >
                                <HiOutlinePlus className="icon" />
                                Show More
                              </button>
                            </div>
                          )}
                      </div>
                    </div>
                    <div
                      className="fw-input-group"
                      style={{ position: "relative" }}
                    >
                      <label className="fw-input-label">
                        The Answer (Root Cause)
                      </label>
                      <div className="fw-input-wrapper">
                        <textarea
                          className="fw-input"
                          value={why}
                          onChange={(e) =>
                            handleFiveWhyChange(index, e.target.value)
                          }
                          placeholder="Type answer here..."
                          rows={2}
                        />
                        <button
                          className="btn-generate-answer"
                          onClick={() => handleSuggestWhy(index)}
                          title="Generate with AI"
                        >
                          <span style={{ marginRight: "4px" }}>Generate</span>
                          <HiOutlineSparkles className="icon" />
                        </button>
                      </div>

                      {/* AI Suggestions Dropdown */}
                      {activeSuggestionIndex === index &&
                        suggestions.length > 0 && (
                          <div className="ai-suggestions-dropdown">
                            <div className="ai-suggestions-header">
                              <HiOutlineSparkles
                                style={{ color: "var(--primary)" }}
                              />
                              <span className="title">AI Suggestions</span>
                              <button
                                style={{
                                  marginLeft: "auto",
                                  border: "none",
                                  background: "none",
                                  cursor: "pointer",
                                }}
                                onClick={closeSuggestions}
                              >
                                <HiOutlineXMark />
                              </button>
                            </div>
                            <ul className="ai-suggestion-list">
                              {suggestions.map((s, i) => (
                                <li key={i}>
                                  <button
                                    className="ai-suggestion-item"
                                    onClick={() =>
                                      handleSelectSuggestion(index, s)
                                    }
                                  >
                                    <HiOutlineArrowRight className="icon" />
                                    {s}
                                  </button>
                                </li>
                              ))}
                            </ul>
                            <button
                              className="ai-show-more-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleSuggestWhy(index, true); // true = append mode
                              }}
                              disabled={generatingIndex === index}
                            >
                              <HiOutlinePlus className="icon" />
                              Show More
                            </button>
                          </div>
                        )}
                    </div>
                  </div>
                </div>

                {index < fiveWhys.length - 1 && (
                  <div className="fw-connector">
                    <HiOutlineArrowDown />
                  </div>
                )}
              </div>
            ))}
          </div>

          {fiveWhys.length < 9 && (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "1rem",
                position: "relative",
                zIndex: 10,
              }}
            >
              <button className="btn-fw-secondary" onClick={addWhy}>
                <HiOutlineCheckCircle /> Add Next Why
              </button>
            </div>
          )}
        </div>

        {/* Root Cause & Validation Section */}
        <div className="fw-card" style={{ marginTop: "2rem" }}>
          <div className="fw-card-header">
            <h3 className="fw-card-title">Root Cause Validation & Actions</h3>
          </div>
          <div style={{ padding: "1.5rem" }}>
            {/* Derived Root Cause */}
            <div className="fw-input-group" style={{ marginBottom: "1.5rem" }}>
              <label className="fw-input-label">
                Identified Root Cause (Last Why)
              </label>
              <div
                className="fw-input"
                style={{
                  backgroundColor: "var(--slate-50)",
                  minHeight: "60px",
                  padding: "0.75rem",
                }}
              >
                {fiveWhys.filter((w) => w && w.trim()).pop() ||
                  "Pending Analysis..."}
              </div>
            </div>

            {/* Validation Matrix */}
            <div style={{ overflowX: "auto", marginBottom: "1.5rem" }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: "0.875rem",
                }}
              >
                <thead>
                  <tr>
                    <th
                      style={{
                        border: "1px solid var(--slate-300)",
                        padding: "0.5rem",
                        textAlign: "left",
                      }}
                    >
                      5. Root cause is one of the following 5 items.
                    </th>
                    {["JH", "PM", "Design", "E&T", "Skill"].map((h) => (
                      <th
                        key={h}
                        style={{
                          border: "1px solid var(--slate-300)",
                          padding: "0.5rem",
                          textAlign: "center",
                          width: "80px",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      key: "poorBasicCondition",
                      label: "Poor Basic Condition",
                    },
                    {
                      key: "poorOperatingCondition",
                      label: "Poor Operating Condition",
                    },
                    { key: "deterioration", label: "Deterioration" },
                    { key: "weakDesign", label: "Weak Design" },
                    { key: "poorSkill", label: "Poor Skill" },
                  ].map((row) => (
                    <tr key={row.key}>
                      <td
                        style={{
                          border: "1px solid var(--slate-300)",
                          padding: "0.5rem",
                        }}
                      >
                        {row.label}
                      </td>
                      {["jh", "pm", "design", "et", "skill"].map((col) => (
                        <td
                          key={col}
                          style={{
                            border: "1px solid var(--slate-300)",
                            padding: "0.5rem",
                            textAlign: "center",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={rootCauseValidation[row.key][col]}
                            onChange={(e) =>
                              setRootCauseValidation((prev) => ({
                                ...prev,
                                [row.key]: {
                                  ...prev[row.key],
                                  [col]: e.target.checked,
                                },
                              }))
                            }
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                            }}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Action Items */}
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "2rem",
                marginBottom: "1.5rem",
                padding: "1rem",
                backgroundColor: "var(--slate-50)",
                borderRadius: "var(--border-radius-lg)",
              }}
            >
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontWeight: 600,
                }}
              >
                <strong style={{ marginRight: "0.5rem" }}>6.</strong>
                <input
                  type="checkbox"
                  checked={actionItems.kaizen}
                  onChange={(e) =>
                    setActionItems((prev) => ({
                      ...prev,
                      kaizen: e.target.checked,
                    }))
                  }
                  style={{ width: "18px", height: "18px" }}
                />{" "}
                Kaizen
                {actionItems.kaizen && (
                  <button
                    className="btn-icon"
                    onClick={handleNavigateToKaizen}
                    title="Open Kaizen Entry"
                    style={{
                      marginLeft: "0.5rem",
                      color: "var(--primary)",
                      border: "1px solid var(--slate-300)",
                      borderRadius: "6px",
                      padding: "4px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "white",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    <HiOutlineArrowTopRightOnSquare />
                  </button>
                )}
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontWeight: 600,
                }}
              >
                <input
                  type="checkbox"
                  checked={actionItems.capa}
                  onChange={(e) =>
                    setActionItems((prev) => ({
                      ...prev,
                      capa: e.target.checked,
                    }))
                  }
                  style={{ width: "18px", height: "18px" }}
                />{" "}
                CAPA
                {actionItems.capa && (
                  <button
                    className="btn-icon"
                    onClick={handleNavigateToCAPA}
                    title="Open CAPA Entry"
                    style={{
                      marginLeft: "0.5rem",
                      color: "var(--primary)",
                      border: "1px solid var(--slate-300)",
                      borderRadius: "6px",
                      padding: "4px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "white",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    <HiOutlineArrowTopRightOnSquare />
                  </button>
                )}
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontWeight: 600,
                }}
              >
                <input
                  type="checkbox"
                  checked={actionItems.opl}
                  onChange={(e) =>
                    setActionItems((prev) => ({
                      ...prev,
                      opl: e.target.checked,
                    }))
                  }
                  style={{ width: "18px", height: "18px" }}
                />{" "}
                One Point Lesson
                {actionItems.opl && (
                  <button
                    className="btn-icon"
                    onClick={handleNavigateToOPL}
                    title="Open OPL Entry"
                    style={{
                      marginLeft: "0.5rem",
                      color: "var(--primary)",
                      border: "1px solid var(--slate-300)",
                      borderRadius: "6px",
                      padding: "4px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: "white",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                  >
                    <HiOutlineArrowTopRightOnSquare />
                  </button>
                )}
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontWeight: 600,
                }}
              >
                <input
                  type="checkbox"
                  checked={actionItems.standards}
                  onChange={(e) =>
                    setActionItems((prev) => ({
                      ...prev,
                      standards: e.target.checked,
                    }))
                  }
                  style={{ width: "18px", height: "18px" }}
                />{" "}
                Included in (TBM / TBI / CBM / PM stds.)
              </label>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  fontWeight: 600,
                }}
              >
                <input
                  type="checkbox"
                  checked={actionItems.pokaYoke}
                  onChange={(e) =>
                    setActionItems((prev) => ({
                      ...prev,
                      pokaYoke: e.target.checked,
                    }))
                  }
                  style={{ width: "18px", height: "18px" }}
                />{" "}
                Poka Yoke
              </label>
            </div>

            {/* Kaizen Idea */}
            <div className="fw-input-group">
              <label className="fw-input-label">7. Kaizen Idea :</label>
              <textarea
                className="fw-input"
                style={{
                  minHeight: "80px",
                  borderBottom: "2px solid var(--slate-300)",
                }}
                placeholder="Describe Kaizen provision..."
                value={kaizenIdea}
                onChange={(e) => setKaizenIdea(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>
      {/* Print View Layout */}
      <div className="print-only">
        <table className="print-table">
          {/* Header Row 1 */}
          <tbody>
            <tr>
              <td colSpan="2" style={{ width: "33%" }}>
                <div style={{ fontWeight: "bold", fontSize: "14px" }}>
                  {recordDetails.companyName}
                </div>
                <div>Puzhal, Chennai.</div>
              </td>
              <td colSpan="2" style={{ width: "33%" }}>
                <span className="print-section-label">Equipment Name : </span>
                {recordDetails.assetName}
              </td>
              <td colSpan="2" style={{ width: "33%" }}>
                <span className="print-section-label">Equipment No: </span>
                {recordDetails.machineCode}
              </td>
            </tr>

            {/* Header Row 2 */}
            <tr>
              <td colSpan="2" className="print-sheet-title">
                WHY - WHY ANALYSIS SHEET
              </td>
              <td style={{ borderRight: "none" }}>
                <span className="print-section-label">Analysis no</span>
              </td>
              <td style={{ borderLeft: "none", fontWeight: "bold" }}>
                {rcaId ? String(rcaId).padStart(2, "0") : nextAnalysisNo}
              </td>
              <td>
                <span className="print-section-label">Date & Shift</span>
                <br />
                {recordDetails.createdDate} & {recordDetails.shift}
              </td>
              <td>
                <span className="print-section-label">Action done by :</span>
                <br />
                <div style={{ textAlign: "center", fontWeight: "bold" }}>
                  {recordDetails.createdBy}
                </div>
              </td>
            </tr>

            {/* Loss Name Row */}
            <tr>
              <td colSpan="4">
                <span className="print-section-label">Loss Name : </span>
                {extraData.lossName}
              </td>
              <td colSpan="2">
                <div style={{ fontSize: "11px" }}>
                  In case of spare part replacement{" "}
                  <div
                    className={`print-checkbox ${extraData.spareParts === "replacement" ? "checked" : ""
                      }`}
                  ></div>
                </div>
              </td>
            </tr>

            {/* Problem Phenomenon Label */}
            <tr>
              <td
                colSpan="4"
                className="print-section-label"
                style={{ borderBottom: "none" }}
              >
                1. Breakdown, Physical phenomenon:
              </td>
              <td colSpan="2" rowSpan="2" style={{ verticalAlign: "middle" }}>
                <div style={{ fontSize: "11px" }}>
                  In case of spare part No replacement{" "}
                  <div
                    className={`print-checkbox ${extraData.spareParts === "no" ? "checked" : ""
                      }`}
                  ></div>
                </div>
              </td>
            </tr>
            {/* Problem Phenomenon Value */}
            <tr>
              <td
                colSpan="4"
                style={{
                  height: "40px",
                  borderTop: "none",
                  fontWeight: "bold",
                }}
              >
                {formData.problemStatement}
              </td>
            </tr>

            {/* Immediate Action */}
            <tr>
              <td colSpan="3" style={{ borderBottom: "none" }}>
                <span className="print-section-label">
                  2. What is your immediate action? (For correcting the problem)
                </span>
                <div style={{ fontWeight: "bold", marginTop: "4px" }}>
                  {extraData.immediateAction}
                </div>
              </td>
              <td colSpan="3" style={{ borderBottom: "none" }}>
                <span className="print-section-label">
                  2.1 How immediate action concluded : (for arriving at the
                  problem)
                </span>
                <div style={{ fontWeight: "bold", marginTop: "4px" }}>
                  {extraData.actionConclusion}
                </div>
              </td>
            </tr>
            <tr>
              <td
                colSpan="3"
                style={{ height: "20px", borderTop: "none" }}
              ></td>
              <td
                colSpan="3"
                style={{ height: "20px", borderTop: "none" }}
              ></td>
            </tr>

            {/* 5 Whys Table Header */}
            <tr>
              <td
                style={{
                  width: "5%",
                  textAlign: "center",
                  fontWeight: "bold",
                }}
              >
                4. <br />
                WHY
              </td>
              <td
                colSpan="2"
                style={{ textAlign: "center", fontWeight: "bold" }}
              >
                Why did you take above action
              </td>
              <td
                colSpan="2"
                style={{ textAlign: "center", fontWeight: "bold" }}
              >
                Due to
              </td>
              <td style={{ textAlign: "center", fontWeight: "bold" }}>
                Action
              </td>
            </tr>

            {/* 5 Whys Rows */}
            {[0, 1, 2, 3, 4].map((i) => (
              <tr key={i} style={{ height: "35px" }}>
                <td style={{ textAlign: "center", fontWeight: "bold" }}>
                  {i + 1}
                </td>
                <td colSpan="2" style={{ verticalAlign: "middle" }}>
                  {whyQuestions[i]}
                </td>
                <td colSpan="2" style={{ verticalAlign: "middle" }}>
                  {fiveWhys[i]}
                </td>
                <td></td>
              </tr>
            ))}

            {/* Root Cause */}
            <tr>
              <td colSpan="6">
                <span className="print-section-label">ROOT CAUSE : </span>
                <span style={{ fontWeight: "bold" }}>
                  {fiveWhys.filter((w) => w && w.trim()).pop() || ""}
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        {/* Root Cause Validation Matrix - Separate Table for better column control */}
        <table
          className="print-table"
          style={{ marginTop: "-9px", borderTop: "none" }}
        >
          <colgroup>
            <col style={{ width: "50%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "10%" }} />
          </colgroup>
          <tbody>
            <tr>
              <td className="print-section-label">
                5. Root cause is one of the following 5 items.
              </td>
              {["JH", "PM", "Design", "E&T", "Skill"].map((h) => (
                <td
                  key={h}
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    fontSize: "10pt",
                  }}
                >
                  {h}
                </td>
              ))}
            </tr>
            {/* Matrix Rows */}
            {[
              { key: "poorBasicCondition", label: "Poor Basic Condition" },
              {
                key: "poorOperatingCondition",
                label: "Poor operating Condition",
              },
              { key: "deterioration", label: "Deterioration" },
              { key: "weakDesign", label: "Weak design" },
              { key: "poorSkill", label: "Poor Skill" },
            ].map((row, idx) => (
              <tr key={row.key}>
                <td style={{ height: "20px" }}>
                  <div className="print-flex-row">
                    <div
                      className={`print-checkbox ${Object.values(rootCauseValidation[row.key]).some(
                        (v) => v,
                      )
                        ? "checked"
                        : ""
                        }`}
                    ></div>
                    {row.label}
                  </div>
                </td>
                {["jh", "pm", "design", "et", "skill"].map((col) => (
                  <td key={col} style={{ textAlign: "center" }}>
                    {rootCauseValidation[row.key][col] ? "✓" : ""}
                  </td>
                ))}
              </tr>
            ))}

            {/* Action Items & Kaizen */}
            <tr>
              <td colSpan="6" style={{ padding: "0.5rem" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    marginBottom: "8px",
                  }}
                >
                  <div className="print-flex-row">
                    <strong>6</strong>
                    <span style={{ marginLeft: "8px" }}>Kaizen</span>
                    <div
                      className={`print-checkbox ${actionItems.kaizen ? "checked" : ""
                        }`}
                    ></div>
                  </div>
                  <div className="print-flex-row">
                    <span>CAPA</span>
                    <div
                      className={`print-checkbox ${actionItems.capa ? "checked" : ""
                        }`}
                    ></div>
                  </div>
                  <div className="print-flex-row">
                    <span>One Point Lesson</span>
                    <div
                      className={`print-checkbox ${actionItems.opl ? "checked" : ""
                        }`}
                    ></div>
                  </div>
                  <div className="print-flex-row">
                    <span>Included in (TBM / TBI / CBM / PM stds.)</span>
                    <div
                      className={`print-checkbox ${actionItems.standards ? "checked" : ""
                        }`}
                    ></div>
                  </div>
                  <div className="print-flex-row">
                    <span>Poka Yoke</span>
                    <div
                      className={`print-checkbox ${actionItems.pokaYoke ? "checked" : ""
                        }`}
                    ></div>
                  </div>
                </div>
              </td>
            </tr>
            <tr>
              <td colSpan="6" style={{ height: "40px" }}>
                <span className="print-section-label">7. Kaizen Idea : </span>
                {kaizenIdea}
              </td>
            </tr>

            {/* Footer with signatures (optional based on format, but usually typically there) */}
            <tr>
              <td colSpan="6" style={{ border: "none", padding: "10px 0" }}>
                <div
                  style={{
                    fontSize: "10pt",
                    fontWeight: "bold",
                    marginTop: "5px",
                  }}
                >
                  (Note: Please fillup this form in pencil immediately after m/c
                  is started)
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FiveWhysAnalysis;
